import { Directive, ElementRef, inject, OnDestroy, OnInit, Renderer2, ViewContainerRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FeedbackService } from '../../services/feedback.service';
import { NotificationComponent } from '../components/notification/notification.component';

@Directive({
  selector: '[appNotificationContainer]',
  standalone: true
})
export class NotificationContainerDirective implements OnInit, OnDestroy {
  private readonly _elementRef = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _feedbackService = inject(FeedbackService);
  private readonly _destroy$ = new Subject<void>();

  private _notificationElements = new Map<string, HTMLElement>();

  ngOnInit(): void {
    // Set up the container styling
    this.setupContainerStyling();

    // Subscribe to notifications
    this._feedbackService.notifications$.pipe(takeUntil(this._destroy$)).subscribe((notifications) => {
      this.updateNotifications(notifications);
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private setupContainerStyling(): void {
    const element = this._elementRef.nativeElement;

    // Add the container classes
    this._renderer.addClass(element, 'pointer-events-none');
    this._renderer.addClass(element, 'fixed');
    this._renderer.addClass(element, 'top-4');
    this._renderer.addClass(element, 'left-1/2');
    this._renderer.addClass(element, 'z-50');
    this._renderer.addClass(element, '-translate-x-1/2');
    this._renderer.addClass(element, 'transform');
    this._renderer.addClass(element, 'space-y-2');

    // Add animation styles
    this.addAnimationStyles();
  }

  private addAnimationStyles(): void {
    // Check if styles already exist
    if (document.getElementById('notification-animation-styles')) {
      return;
    }

    const style = this._renderer.createElement('style');
    this._renderer.setAttribute(style, 'id', 'notification-animation-styles');
    this._renderer.appendChild(
      style,
      this._renderer.createText(`
      @keyframes slide-in-from-top {
        0% {
          transform: translateY(-100%);
          opacity: 0;
        }
        100% {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .animate-slide-in-from-top {
        animation: slide-in-from-top 0.4s ease-out forwards;
      }
    `)
    );
    this._renderer.appendChild(document.head, style);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private updateNotifications(notifications: any[]): void {
    // Get current notification IDs
    const currentIds = new Set(notifications.map((n) => n.id));

    // Remove notifications that no longer exist
    this._notificationElements.forEach((element, id) => {
      if (!currentIds.has(id)) {
        this._renderer.removeChild(this._elementRef.nativeElement, element);
        this._notificationElements.delete(id);
      }
    });

    // Add or update existing notifications
    notifications.forEach((notification) => {
      if (!this._notificationElements.has(notification.id)) {
        this.createNotificationElement(notification);
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private createNotificationElement(notification: any): void {
    // Create wrapper div
    const wrapperDiv = this._renderer.createElement('div');
    this._renderer.addClass(wrapperDiv, 'pointer-events-auto');
    this._renderer.addClass(wrapperDiv, 'animate-slide-in-from-top');

    // Create notification component dynamically
    const componentRef = this._viewContainerRef.createComponent(NotificationComponent);

    // Set component inputs
    componentRef.setInput('title', notification.title);
    componentRef.setInput('description', notification.description);
    componentRef.setInput('mode', notification.mode);

    // Subscribe to close event
    componentRef.instance.closeToast.subscribe(() => {
      this.removeNotification(notification.id);
    });

    // Append component to wrapper
    this._renderer.appendChild(wrapperDiv, componentRef.location.nativeElement);

    // Append wrapper to container
    this._renderer.appendChild(this._elementRef.nativeElement, wrapperDiv);

    // Store reference
    this._notificationElements.set(notification.id, wrapperDiv);
  }

  private removeNotification(id: string): void {
    this._feedbackService.remove(id);
  }
}
